/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { useParams } from 'js/lib/useRouter';

import { Button, Dialog, InlineNotification, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { useNaptime } from 'bundles/naptimejs';
import ProgramCurriculumsV1 from 'bundles/naptimejs/resources/programCurriculums.v1';

import _t from 'i18n!nls/enterprise-admin-program-catalog';

type Props = {
  variant?: 'collection' | 'learningPath' | 'role';
  onConfirmation: () => Promise<void>;
  onClose: () => void;
};

const DeleteCollectionModal = ({ variant = 'collection', onConfirmation, onClose }: Props) => {
  const { HeadingGroup, Actions } = Dialog;
  const track = useTracker();
  const { thirdPartySlug, programSlug } = useParams();
  const [hasError, setHasError] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { naptime } = useNaptime(() => ({}));
  const handleOnConfirmation = async () => {
    setIsSaving(true);
    if (variant === 'learningPath') {
      track('click_button', {
        button: { name: 'enterprise_delete_learning_path' },
        enterprise: {
          ...(thirdPartySlug ? { organizationSlug: thirdPartySlug } : {}),
          ...(programSlug ? { programSlug } : {}),
        },
      });
    }
    try {
      setHasError(false);
      await onConfirmation();
      await naptime.refreshData({
        resources: [ProgramCurriculumsV1.RESOURCE_NAME],
      });
    } catch {
      setHasError(true);
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={true} onClose={onClose} variant="standard" data-e2e="delete-collection-from-catalog-modal">
      <HeadingGroup>
        {
          {
            collection: _t('Are you sure you want to delete this collection?'),
            learningPath: _t('Are you sure you want to delete this learning path?'),
            role: _t('Are you sure you want to delete this role?'),
          }[variant]
        }
      </HeadingGroup>
      {variant === 'learningPath' && (
        <Dialog.Content>
          <Typography2 component="p">
            {_t(
              "You’ll lose all reporting on this learning path and learners will no longer see it on their homepage discovery experience or be able to receive a badge. However, they'll remain enrolled in any content they've already started within the path."
            )}
          </Typography2>
        </Dialog.Content>
      )}
      <Actions>
        {hasError && (
          <InlineNotification
            severity="error"
            css={css`
              margin-bottom: var(--cds-spacing-200);
            `}
          >
            {_t('Something went wrong. Please try again')}
          </InlineNotification>
        )}
        <div
          css={css`
            display: flex;
            gap: var(--cds-spacing-200);
          `}
        >
          <Button
            size="medium"
            variant="primary"
            onClick={handleOnConfirmation}
            data-e2e="delete-collection-yes-button"
            css={{ marginRight: 'var(--cds-spacing-200)' }}
            iconPosition="before"
            loading={isSaving}
          >
            {
              {
                collection: _t('Delete collection'),
                learningPath: _t('Delete learning path'),
                role: _t('Delete role'),
              }[variant]
            }
          </Button>
          <Button size="medium" variant="secondary" onClick={onClose}>
            {_t('Cancel')}
          </Button>
        </div>
      </Actions>
    </Dialog>
  );
};

export default DeleteCollectionModal;
